// Transport-level helpers the upload stores share: a semaphore so every file's
// parts draw from ONE pool of connections, served file by file, a retry loop that waits out
// an offline stretch instead of burning attempts on it, and a stall guard for
// a part PUT whose bytes stop moving.
//
// Browsers allow ~6 connections per host. Four parts in flight PER FILE with
// several files added at once meant most PUTs sat in the browser's queue, hit
// the server's idle timeout, and reported "network error" — the upload was
// not failing, it was starving.

import { UploadApiError } from './api';

/**
 * Counting semaphore, served by rank and then in the order asked.
 *
 * The upload stores rank a file by when it was added, so a permit goes to the
 * earliest file that still has a part waiting: files FINISH one after another,
 * and each starts converting as soon as its bytes are in, instead of every
 * file creeping along together and all of them finishing at the end. A later
 * file still uses any permit an earlier one cannot (between its parts, while
 * it completes), so nothing sits idle.
 *
 * A released permit is handed on after the current turn, not at once: the part
 * that just finished asks for its file's next one in the same breath, and must
 * find the permit still there, ahead of a later file.
 */
export class Semaphore {
  private active = 0;
  private cap: number;
  private seq = 0;
  private readonly waiting: Array<{ grant: () => void; rank: number; seq: number; signal?: AbortSignal; onAbort?: () => void }> = [];

  constructor(capacity: number) {
    this.cap = Semaphore.clamp(capacity);
  }

  private static clamp(n: number): number {
    // NaN or undefined would make `active < capacity` false forever: every
    // waiter would hang with no error. Fall back to one permit instead.
    return Number.isFinite(n) ? Math.max(1, Math.floor(n)) : 1;
  }

  get capacity(): number {
    return this.cap;
  }

  get inFlight(): number {
    return this.active;
  }

  get queued(): number {
    return this.waiting.length;
  }

  /** Raising the capacity hands out the new permits at once. */
  setCapacity(n: number): void {
    this.cap = Semaphore.clamp(n);
    this.pump();
  }

  /** Resolves with the release function once a permit is free. A lower `rank` is served first. */
  acquire(signal?: AbortSignal, rank = 0): Promise<() => void> {
    if (signal?.aborted) return Promise.reject(abortError());
    return new Promise<() => void>((resolve, reject) => {
      const entry: { grant: () => void; rank: number; seq: number; signal?: AbortSignal; onAbort?: () => void } = {
        grant: () => {
          this.active++;
          let released = false;
          resolve(() => {
            if (released) return;
            released = true;
            this.active--;
            this.pumpSoon();
          });
        },
        rank,
        seq: this.seq++,
        signal,
      };
      if (signal) {
        entry.onAbort = () => {
          const i = this.waiting.indexOf(entry);
          if (i >= 0) this.waiting.splice(i, 1);
          reject(abortError());
        };
        signal.addEventListener('abort', entry.onAbort, { once: true });
      }
      this.waiting.push(entry);
      this.pump();
    });
  }

  // One timer per release, not one shared: pump is idempotent, and a shared
  // "already scheduled" flag stays set for good if its timer never fires (a
  // test's fake timers discarded), and then no permit is ever handed on again.
  private pumpSoon(): void {
    setTimeout(() => this.pump(), 0);
  }

  private pump(): void {
    while (this.active < this.cap && this.waiting.length) {
      let best = 0;
      for (let i = 1; i < this.waiting.length; i++) {
        const w = this.waiting[i];
        const b = this.waiting[best];
        if (w.rank < b.rank || (w.rank === b.rank && w.seq < b.seq)) best = i;
      }
      const [next] = this.waiting.splice(best, 1);
      if (next.signal && next.onAbort) next.signal.removeEventListener('abort', next.onAbort);
      next.grant();
    }
  }
}

export function abortError(): DOMException {
  return new DOMException('aborted', 'AbortError');
}

export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/** A PUT whose bytes stopped moving; retried like a dropped connection. */
export class StallError extends Error {
  constructor(readonly afterMs: number) {
    super(`no progress for ${Math.round(afterMs / 1000)} s`);
  }
}

/**
 * Whether an error is the kind that goes away by itself: the connection
 * dropped, a stall, a gateway hiccup, or the server asking us to slow down.
 * A 4xx that is not one of those is the server's verdict and stays.
 */
export function isTransient(err: unknown): boolean {
  if (err instanceof StallError) return true;
  if (err instanceof UploadApiError) {
    return err.status === 0 || err.status === 408 || err.status === 425 || err.status === 429 || err.status >= 500;
  }
  // fetch() rejects with a TypeError when the request never got an answer
  // ("Failed to fetch", "NetworkError…", "Load failed"). Any other TypeError
  // is a bug, and retrying a bug only hides it.
  return err instanceof TypeError && /fetch|network|load failed/i.test(err.message);
}

/** Exponential backoff with jitter: 1 s doubling, capped at 30 s. */
export function backoffMs(attempt: number): number {
  const base = Math.min(30_000, 1000 * 2 ** attempt);
  return base + Math.random() * base * 0.25;
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(abortError());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** How long a retry waits for `online` before trying anyway. */
export const ONLINE_WAIT_CAP_MS = 60_000;

/**
 * Resolves once the browser says it is online; at once when it already is.
 *
 * Capped: `navigator.onLine` is a hint, and some hosts (VPNs, virtual
 * adapters) report offline while requests go through fine. After the cap
 * the caller tries anyway — a real outage costs it one attempt, a false
 * "offline" costs nothing but the wait.
 */
export function waitOnline(signal?: AbortSignal, capMs = ONLINE_WAIT_CAP_MS): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const done = () => {
      if (t) clearTimeout(t);
      window.removeEventListener('online', done);
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    function onAbort() {
      if (t) clearTimeout(t);
      window.removeEventListener('online', done);
      reject(abortError());
    }
    // Infinity means "however long": setTimeout would treat it as 0.
    const t = Number.isFinite(capMs) ? setTimeout(done, capMs) : undefined;
    window.addEventListener('online', done, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export interface RetryOptions {
  /** Total tries, the first included. */
  attempts: number;
  signal?: AbortSignal;
  /** Defaults to isTransient. Return false to stop and rethrow at once. */
  retryable?: (err: unknown, attempt: number) => boolean;
}

/**
 * Run `fn` until it succeeds or the budget is spent. Between tries it waits
 * for the browser to be online (that wait costs no attempt) and then backs
 * off. An abort stops everything immediately; the error thrown after the
 * budget is the original one, so callers can still read its status.
 */
export async function withRetry<T>(fn: (attempt: number, signal?: AbortSignal) => Promise<T>, o: RetryOptions): Promise<T> {
  const retryable = o.retryable ?? isTransient;
  for (let attempt = 0; ; attempt++) {
    if (o.signal?.aborted) throw abortError();
    try {
      return await fn(attempt, o.signal);
    } catch (err) {
      if (isAbort(err) || o.signal?.aborted) throw abortError();
      if (!retryable(err, attempt) || attempt + 1 >= o.attempts) throw err;
      await waitOnline(o.signal);
      await sleep(backoffMs(attempt), o.signal);
    }
  }
}

export interface StallWindows {
  /** No progress for this long while bytes are still being handed over → stall. */
  stallMs: number;
  /**
   * Once every byte has been handed to the socket, progress events stop
   * while the tail drains and the server answers. On a slow uplink that
   * silence is normal and can outlast `stallMs`, so a longer window applies.
   */
  finalMs: number;
}

/**
 * Run a transfer that reports progress; abort it with a StallError when no
 * progress arrives within the window. `run` receives the signal to pass to
 * the request and a `progressed(final?)` callback to call on every progress
 * event — `final` once the last byte has been handed over.
 */
export async function guardStall<T>(
  run: (signal: AbortSignal, progressed: (final?: boolean) => void) => Promise<T>,
  windows: StallWindows,
  parent?: AbortSignal,
): Promise<T> {
  const ctl = new AbortController();
  let stalled = false;
  let last = Date.now();
  let win = windows.stallMs;
  let timer: ReturnType<typeof setTimeout> | undefined;
  // One timer, re-armed from the timestamp when it fires — progress events
  // arrive many times a second, and re-creating a timer on each was waste.
  const check = () => {
    const idle = Date.now() - last;
    if (idle >= win) {
      stalled = true;
      ctl.abort();
      return;
    }
    timer = setTimeout(check, win - idle);
  };
  const progressed = (final?: boolean) => {
    last = Date.now();
    if (final) win = windows.finalMs;
  };
  const onParentAbort = () => ctl.abort();
  if (parent?.aborted) throw abortError();
  parent?.addEventListener('abort', onParentAbort, { once: true });
  timer = setTimeout(check, win);
  try {
    return await run(ctl.signal, progressed);
  } catch (err) {
    if (stalled && !parent?.aborted) throw new StallError(win);
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
    parent?.removeEventListener('abort', onParentAbort);
  }
}
